import React,{useEffect,useMemo,useState} from "react";
import {Modal,Select,Checkbox,DatePicker,Button,Alert,Typography} from "antd";
import {FilePdfOutlined,DownloadOutlined} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import {API_BASE} from "./config/api";
import {getClientToken,handleClientAuthFailure} from "./config/auth";

const {Text}=Typography;
const auth=()=>({headers:{"Content-Type":"application/json","X-KG-Client-Authorization":`Bearer ${getClientToken()}`}});

export default function ClientStatementModal({open,onClose,onDownloads}){
  const fyStart=()=>{const n=dayjs();const y=n.month()>=3?n.year():n.year()-1;return dayjs(`${y}-04-01`);};
  const [scope,setScope]=useState(null);
  const [scopeLoading,setScopeLoading]=useState(false);
  const [selected,setSelected]=useState("__FAMILY__");
  const [sections,setSections]=useState({summary:true,scheme_summary:false,amc_summary:false,child_pan_summary:false,transactions:false});
  const [startDate,setStartDate]=useState(fyStart()),[endDate,setEndDate]=useState(dayjs());
  const [asOnDate,setAsOnDate]=useState(dayjs());
  const [submitting,setSubmitting]=useState(false),[submitted,setSubmitted]=useState(false),[error,setError]=useState("");

  useEffect(()=>{if(!open)return;let alive=true;setScopeLoading(true);setError("");setSubmitted(false);
    axios.get(`${API_BASE}/client_statement_options.php`,auth()).then(r=>{
      if(!alive)return;const d=r.data||{};setScope(d);
      setSelected(d.is_family?"__FAMILY__":(d.master_pan||""));
      setSections(s=>({...s,child_pan_summary:false}));setAsOnDate(dayjs());
    }).catch(e=>{if(handleClientAuthFailure(e.response,e.response?.data))return;if(alive)setError(e?.response?.data?.message||"Unable to load statement options.");})
    .finally(()=>alive&&setScopeLoading(false));return()=>{alive=false};
  },[open]);

  const dateRuleError=useMemo(()=>{
    if(asOnDate&&asOnDate.isAfter(dayjs(),"day"))return "As On Date Cannot be a future date.";
    if(!sections.transactions)return "";
    if(!startDate||!endDate)return "Start Date and End Date are required for Transaction Details.";
    if(startDate.isAfter(endDate,"day"))return "Start Date Cannot be greater than End Date.";
    if(endDate.isBefore(startDate,"day"))return "End Date Cannot be earlier than Start Date.";
    if(endDate.isAfter(asOnDate,"day"))return "End Date Cannot be greater than As On Date.";
    if(asOnDate.isBefore(endDate,"day"))return "As On Date Cannot be earlier than End Date.";
    return "";
  },[sections.transactions,startDate,endDate,asOnDate]);
  const count=useMemo(()=>Object.values(sections).filter(Boolean).length,[sections]);
  const setSec=(k,v)=>{
   if(k==="transactions"&&v){
     const adjustedEnd=endDate.isAfter(asOnDate,"day")?asOnDate:endDate;
     setEndDate(adjustedEnd);
     if(startDate.isAfter(adjustedEnd,"day"))setStartDate(adjustedEnd);
   }
   setSections(s=>({...s,[k]:v}));
 };
 const changeAsOnDate=(v)=>{
   if(!v)return;
   setAsOnDate(v);
   if(sections.transactions){
     const adjustedEnd=endDate.isAfter(v,"day")?v:endDate;
     if(endDate.isAfter(v,"day"))setEndDate(adjustedEnd);
     if(startDate.isAfter(adjustedEnd,"day"))setStartDate(adjustedEnd);
   }
 };
  const onScopeChange=(v)=>{setSelected(v);if(v!=="__FAMILY__")setSections(s=>({...s,child_pan_summary:false}));};

  const generate=async()=>{if(submitting)return;setError("");
    if(!count)return setError("Select at least one report section.");
  if(dateRuleError)return setError(dateRuleError);
    setSubmitting(true);
    try{
      await axios.post(`${API_BASE}/client_statement_job_start.php`,{
        selected_pan:scope?.is_family&&selected!=="__FAMILY__"?selected:null,
        whole_family:!!scope?.is_family&&selected==="__FAMILY__",
        sections:{...sections,child_pan_summary:!!scope?.is_family&&selected==="__FAMILY__"&&!!sections.child_pan_summary},
        as_on_date:asOnDate.format("YYYY-MM-DD"),
        transaction_start_date:sections.transactions?startDate.format("YYYY-MM-DD"):null,
        transaction_end_date:sections.transactions?endDate.format("YYYY-MM-DD"):null
      },auth());
      setSubmitted(true);
    }catch(e){if(handleClientAuthFailure(e.response,e.response?.data))return;setError(e?.response?.data?.message||"Could not submit statement request.");}
    finally{setSubmitting(false);}
  };

  const close=()=>{if(submitting)return;setSubmitted(false);setError("");onClose();};

  return <Modal open={open} centered title="Generate Statement" onCancel={close} closable={!submitting} maskClosable={!submitting} footer={null} width={520} destroyOnClose>
    {submitted?<div style={{padding:"6px 0"}}>
      <Alert type="success" showIcon message="Statement Request Submitted"
        description="Your PDF is being prepared. Once ready, you can download it from My Downloads."/>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
        <Button onClick={close}>Close</Button>
        <Button type="primary" icon={<DownloadOutlined/>} onClick={()=>{close();onDownloads();}}>Go to My Downloads</Button>
      </div>
    </div>:<>
      {scope?.is_family&&<div style={{marginBottom:16}}>
        <Text strong>Statement For</Text>
        <Select loading={scopeLoading} value={selected} onChange={onScopeChange} style={{width:"100%",marginTop:6}}
          options={[{value:"__FAMILY__",label:scope.family_name||"Entire Family"},...(scope.members||[]).map(m=>({value:m.pan,label:m.name}))]}/>
      </div>}
      {!scope?.is_family&&scope?.client_name&&<div style={{marginBottom:14}}><Text strong>{scope.client_name}</Text></div>}
      <div style={{border:"1px solid #dce6f2",borderRadius:12,padding:12,background:"#fbfdff"}}>
        <Text strong>Report Contents</Text>
        <div style={{display:"grid",gap:10,marginTop:10}}>
          <Checkbox checked={sections.summary} onChange={e=>setSec("summary",e.target.checked)}>Summary Only</Checkbox>
          <Checkbox checked={sections.scheme_summary} onChange={e=>setSec("scheme_summary",e.target.checked)}>Scheme Summary</Checkbox>
          <Checkbox checked={sections.amc_summary} onChange={e=>setSec("amc_summary",e.target.checked)}>AMC Summary</Checkbox>
          {scope?.is_family&&selected==="__FAMILY__"&&<Checkbox checked={sections.child_pan_summary} onChange={e=>setSec("child_pan_summary",e.target.checked)}>Child PAN Wise Summary</Checkbox>}
          <Checkbox checked={sections.transactions} onChange={e=>setSec("transactions",e.target.checked)}>Transaction Details</Checkbox>
        </div>
        {sections.transactions&&<div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #e6edf5"}}>
          <Text strong>Transaction Period</Text>
          <div style={{display:"flex",gap:8,marginTop:8}}>
            <DatePicker value={startDate} onChange={v=>v&&setStartDate(v)} status={dateRuleError&&startDate?.isAfter(endDate,"day")?"error":undefined} disabledDate={d=>d&&d.isAfter(endDate,"day")} format="DD-MM-YYYY" style={{width:"50%"}}/>
            <DatePicker value={endDate} onChange={v=>v&&setEndDate(v)} status={dateRuleError&&(endDate?.isBefore(startDate,"day")||endDate?.isAfter(asOnDate,"day"))?"error":undefined} disabledDate={d=>d&&(d.isBefore(startDate,"day")||d.isAfter(asOnDate,"day"))} format="DD-MM-YYYY" style={{width:"50%"}}/>
          </div>
          {dateRuleError&&<div style={{marginTop:7,color:"#ff4d4f",fontSize:12}}>{dateRuleError}</div>}
        </div>}
      </div>
      <div style={{marginTop:14,padding:12,border:"1px solid #dce6f2",borderRadius:12,background:"#fbfdff"}}>
        <Text strong>As On Date</Text>
        <DatePicker value={asOnDate} onChange={changeAsOnDate} status={dateRuleError&&asOnDate?.isAfter(dayjs(),"day")?"error":undefined} disabledDate={d=>d&&d.isAfter(dayjs(),"day")} format="DD-MMM-YYYY" style={{width:"100%",marginTop:8}}/>
        <div style={{marginTop:6}}><Text type="secondary">Report will use the latest available portfolio data on or before the selected date.</Text></div>
      </div>
      {error&&<Alert type="error" showIcon message={error} style={{marginTop:12}}/>}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:18}}>
        <Button onClick={close} disabled={submitting}>Cancel</Button>
        <Button type="primary" icon={<FilePdfOutlined/>} loading={submitting||scopeLoading} disabled={scopeLoading||!scope} onClick={generate}>Generate</Button>
      </div>
    </>}
  </Modal>;
}
